import React, { Component } from 'react'
import { API } from '@getgreenline/homi-shared'
import { inject, observer } from 'mobx-react'
import { Alert, notification, Button, Icon } from 'antd'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { Track } from '../../../../Track'
import moment from 'moment'
import { padStart } from 'lodash'
import { IntegrationStore, Integrators } from '../IntegrationStore'
import { WeedmapApi } from '@getgreenline/weedmaps'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface InjectedProps {
  integrationStore: IntegrationStore<Integrators.WEEDMAP>
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  expiryDate?: number
  isLoading: boolean
  currentDate?: number
}

@inject('currentCompanyStore')
@observer
export class WeedmapsSync extends Component<InjectedProps, State> {
  notificationDurationInSec = 0
  expiryTime = 1000 * 60 * 5 // 5 minutes
  countdownTimer: NodeJS.Timer | undefined
  readonly syncToken = 'weedMapsSyncToken'

  constructor(props: InjectedProps) {
    super(props)
    this.state = {
      expiryDate: undefined,
      isLoading: false,
      currentDate: undefined,
    }
  }

  componentDidMount() {
    this.bootStrapData()
  }

  componentWillUnmount() {
    this.clearTimer()
  }

  syncAllProducts = async () => {
    notification.destroy()

    try {
      this.setState({ isLoading: true })

      const companyId = this.props.currentCompanyStore!.company!.id
      const authToken = window.localStorage.getItem('authToken') || ''

      const result = await WeedmapApi.WeedmapProductApi.productInitSync(companyId, authToken)

      this.setWeedmapsSyncToken()

      Track.track('dashboard:integrations_weedMapsSyncAll')

      notification.success({
        message: 'Sync successful',
        description: (
          <div>
            <div>Sync successfully initiated.</div>
            <div>{`Syncing products may take up to 5 minutes to show up on WeedMaps`}</div>
          </div>
        ),
        duration: this.notificationDurationInSec,
      })
    } catch (error) {
      Track.track('dashboard:integrations_weedMapsSyncAllError')
      notification.error({
        message: 'Sync unsuccessful',
        description: parseErrorMsg(error) || `An error occurred while syncing all products`,
        duration: this.notificationDurationInSec,
      })
    } finally {
      this.setState({ isLoading: false })
    }
  }

  bootStrapData = () => {
    const weedMapsSyncToken = localStorage.getItem(this.syncToken)

    if (weedMapsSyncToken !== null && weedMapsSyncToken !== undefined) {
      const expired = this.isTokenExpired(weedMapsSyncToken)

      if (!expired) {
        this.setState({ expiryDate: JSON.parse(weedMapsSyncToken) })
        this.setTimer()
      } else {
        localStorage.removeItem(this.syncToken)
        this.setState({ expiryDate: undefined })
        this.clearTimer()
      }
    }
  }
  setWeedmapsSyncToken = () => {
    const expiryDate = new Date().getTime() + this.expiryTime
    this.setTimer()
    this.setState({ expiryDate })
    localStorage.setItem(this.syncToken, JSON.stringify(expiryDate))
  }

  setTimer = () => {
    this.countdownTimer = setInterval(() => {
      const currentDate = new Date().getTime()
      this.setState({ currentDate })
    }, 1000)
  }

  clearTimer = () => {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  }

  isTokenExpired = (token: string) => {
    const expiryDate = JSON.parse(token) as number

    if (expiryDate === null) return

    return !(expiryDate > new Date().valueOf())
  }

  get isWeedMapsApiKeyExist() {
    const apiKeys = this.props.integrationStore?.locationApiKeys || []
    return apiKeys.length > 0
  }

  get remainingSyncTime() {
    const expiryDate = this.state.expiryDate

    if (!expiryDate) return

    const remainingTime = Math.max(moment(expiryDate).diff(this.state.currentDate, 'seconds'), 0)

    const min = padStart(`${Math.floor(remainingTime / 60)}`, 2, '0')
    const sec = padStart(`${remainingTime % 60}`, 2, '0')

    return `${min}:${sec}`
  }

  get isButtonDisabled() {
    return !!this.state.expiryDate || this.state.isLoading
  }

  render() {
    return (
      <div>
        <div>
          {!this.isWeedMapsApiKeyExist ? (
            <div className='alert alert-warning'>
              Each location requires its own API key.
              <br />
              Please contact WeedMaps to get your keys.
            </div>
          ) : (
            <>
              <div className='mb-3'>
                {this.remainingSyncTime && (
                  <Alert
                    type='warning'
                    showIcon
                    message='Syncing is processing'
                    description={
                      <div>
                        Please refresh the page after {this.remainingSyncTime} to activate [Sync
                        All] button
                      </div>
                    }
                    style={{ marginBottom: 8 }}
                  />
                )}

                <Alert
                  type='info'
                  showIcon
                  message={<b>Before you proceed...</b>}
                  description={
                    <ul className='no-margin-list'>
                      <li>
                        <b>Sync all</b> will sync <b>all</b> products with weedMaps categories at{' '}
                        <b>all</b> locations with WeedMaps API keys
                      </li>
                      <li>
                        All standalone and variant products in {BLAZE_COMPANY} will be synced as{' '}
                        <b>standalone</b> products on WeedMaps
                      </li>
                      <li>
                        Standalone products and variants with $0 retail price in {BLAZE_COMPANY}{' '}
                        will <b>not</b> show up on WeedMaps
                      </li>
                      <li>
                        Standalone products and variants with 0 or negative quantity in{' '}
                        {BLAZE_COMPANY} will be synced as <b>unpublished</b> products on WeedMaps
                      </li>
                      <li>
                        <strong>Syncing all products may take 5-10 minutes to complete</strong>
                      </li>
                    </ul>
                  }
                />
              </div>

              <Button
                className='mb-3'
                onClick={() => this.syncAllProducts()}
                disabled={this.isButtonDisabled}
                style={{ cursor: this.isButtonDisabled ? 'not-allowed' : 'pointer' }}
              >
                <Icon type='sync' />
                {this.isButtonDisabled ? 'Syncing...' : 'Sync All'}
              </Button>
            </>
          )}
        </div>
      </div>
    )
  }
}
